import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export type Theme =
    | 'default'      // Original dark blue
    | 'midnight'     // Deep purple/blue
    | 'aurora'       // Northern lights gradient
    | 'ocean'        // Deep ocean blue/teal
    | 'forest'       // Dark green/emerald
    | 'sunset'       // Warm orange/pink gradient
    | 'cyber'        // Cyberpunk neon
    | 'minimal';     // Pure dark minimal

export interface ThemeConfig {
    id: Theme;
    name: string;
    nameEn: string;
    nameFr: string;
    description: string;
    descriptionFr: string;
    preview: string; // CSS gradient/color for preview
    interactive: boolean;
}

export const themes: ThemeConfig[] = [
    {
        id: 'default',
        name: 'Default',
        nameEn: 'Default',
        nameFr: 'Par défaut',
        description: 'Classic dark theme with indigo accents',
        descriptionFr: 'Thème sombre classique avec accents indigo',
        preview: 'linear-gradient(135deg, #0a0a0f 0%, #1a1a2e 50%, #16213e 100%)',
        interactive: false,
    },
    {
        id: 'midnight',
        name: 'Midnight',
        nameEn: 'Midnight',
        nameFr: 'Minuit',
        description: 'Deep purple with subtle star particles',
        descriptionFr: 'Violet profond avec particules d\'étoiles subtiles',
        preview: 'linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)',
        interactive: true,
    },
    {
        id: 'aurora',
        name: 'Aurora',
        nameEn: 'Aurora',
        nameFr: 'Aurore',
        description: 'Northern lights with flowing color waves',
        descriptionFr: 'Aurores boréales avec vagues de couleurs fluides',
        preview: 'linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)',
        interactive: true,
    },
    {
        id: 'ocean',
        name: 'Ocean',
        nameEn: 'Ocean',
        nameFr: 'Océan',
        description: 'Deep ocean depths with gentle waves',
        descriptionFr: 'Profondeurs océaniques avec vagues douces',
        preview: 'linear-gradient(135deg, #000428 0%, #004e92 50%, #000428 100%)',
        interactive: true,
    },
    {
        id: 'forest',
        name: 'Forest',
        nameEn: 'Forest',
        nameFr: 'Forêt',
        description: 'Dark emerald with firefly particles',
        descriptionFr: 'Émeraude sombre avec particules de lucioles',
        preview: 'linear-gradient(135deg, #0a1a0a 0%, #1a3a1a 50%, #0d2818 100%)',
        interactive: true,
    },
    {
        id: 'sunset',
        name: 'Sunset',
        nameEn: 'Sunset',
        nameFr: 'Coucher de soleil',
        description: 'Warm gradient with floating light orbs',
        descriptionFr: 'Dégradé chaud avec orbes de lumière flottantes',
        preview: 'linear-gradient(135deg, #1a0a1a 0%, #2d1f3d 50%, #1a1a2e 100%)',
        interactive: true,
    },
    {
        id: 'cyber',
        name: 'Cyber',
        nameEn: 'Cyber',
        nameFr: 'Cyber',
        description: 'Neon cyberpunk with grid animation',
        descriptionFr: 'Cyberpunk néon avec animation de grille',
        preview: 'linear-gradient(135deg, #0a0a0a 0%, #1a0a2e 50%, #0a1a1a 100%)',
        interactive: true,
    },
    {
        id: 'minimal',
        name: 'Minimal',
        nameEn: 'Minimal',
        nameFr: 'Minimaliste',
        description: 'Pure dark theme, no distractions',
        descriptionFr: 'Thème sombre pur, sans distractions',
        preview: 'linear-gradient(135deg, #0a0a0a 0%, #121212 50%, #0a0a0a 100%)',
        interactive: false,
    },
];

interface ThemeState {
    theme: Theme;
    interactiveEnabled: boolean;
    setTheme: (theme: Theme) => void;
    setInteractiveEnabled: (enabled: boolean) => void;
}

export const useThemeStore = create<ThemeState>()(
    persist(
        (set) => ({
            theme: 'midnight',
            interactiveEnabled: true,
            setTheme: (theme) => set({ theme }),
            setInteractiveEnabled: (interactiveEnabled) => set({ interactiveEnabled }),
        }),
        {
            name: 'theme-storage',
            storage: createJSONStorage(() => localStorage),
        }
    )
);

// Get theme config by ID
export function getThemeConfig(theme: Theme): ThemeConfig {
    const found = themes.find(t => t.id === theme);
    if (found !== undefined) return found;
    // Default config if theme not found
    return {
        id: 'default',
        name: 'Default',
        nameEn: 'Default',
        nameFr: 'Par défaut',
        description: 'Classic dark theme with indigo accents',
        descriptionFr: 'Thème sombre classique avec accents indigo',
        preview: 'linear-gradient(135deg, #0a0a0f 0%, #1a1a2e 50%, #16213e 100%)',
        interactive: false,
    };
}
